// Add WhatsApp Subscriber Modal (WP-03 / WP-04).
import { useId, useRef, useState } from 'react';
import { useI18n } from '../../lib/i18n';
import { subscriberCreate } from '../../lib/subscribers';
import { useLabels } from '../../lib/crm';
import { useDialogA11y } from '../../lib/useDialogA11y';
import { CloseIcon, UserPlusIcon, CheckIcon } from '../icons';

interface AddSubscriberModalProps {
  onClose: () => void;
  onSuccess?: () => void;
}

export default function AddSubscriberModal({ onClose, onSuccess }: AddSubscriberModalProps) {
  const { t } = useI18n();
  const { labels } = useLabels();
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useDialogA11y(true, panelRef, onClose);

  const toggleLabel = (labelId: string) => {
    setSelectedLabels((prev) =>
      prev.includes(labelId) ? prev.filter((id) => id !== labelId) : [...prev, labelId],
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanPhone = phone.trim().replace(/[^\d+]/g, '');
    if (!cleanPhone) {
      setError(t('subs.phoneRequired') || 'Phone number is required');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await subscriberCreate({
        phoneE164: cleanPhone.startsWith('+') ? cleanPhone : `+${cleanPhone}`,
        name: name.trim() || undefined,
        labelIds: selectedLabels.length > 0 ? selectedLabels : undefined,
      });
      onSuccess?.();
      onClose();
    } catch (err) {
      setError((err as Error).message || 'Failed to create subscriber');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="w-full max-w-md overflow-hidden rounded-2xl border border-line bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900"
      >
        <div className="flex items-center justify-between border-b border-line px-5 py-4 dark:border-slate-800">
          <div className="flex items-center gap-2.5">
            <div className="grid h-8 w-8 place-items-center rounded-lg bg-brand-50 text-brand dark:bg-brand-950/40 dark:text-brand-tint">
              <UserPlusIcon size={18} />
            </div>
            <div>
              <h3 id={titleId} className="text-sm font-bold text-ink dark:text-slate-100">
                {t('inbox.addSubscriber.title') || 'Add WhatsApp Subscriber'}
              </h3>
              <p className="text-xs text-ink-3 dark:text-slate-400">
                {t('inbox.addSubscriber.subtitle') || 'Quickly add a new subscriber to your contact directory'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close') || 'Close'}
            className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <CloseIcon size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="rounded-lg bg-rose-50 p-3 text-xs font-semibold text-rose-600 dark:bg-rose-950/40 dark:text-rose-400">
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs font-bold text-ink mb-1 dark:text-slate-200">
              {t('subs.field.phone') || 'WhatsApp Phone (E.164)'} <span className="text-rose-500">*</span>
            </label>
            <input
              type="tel"
              required
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+968 9123 4567"
              className="w-full rounded-xl border border-line bg-surface px-3.5 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-800 dark:text-slate-200"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-ink mb-1 dark:text-slate-200">
              {t('subs.field.name') || 'Full Name'}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('inbox.subscriberNamePlaceholder') || 'e.g. Fatima Al-Balushi'}
              className="w-full rounded-xl border border-line bg-surface px-3.5 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-800 dark:text-slate-200"
            />
          </div>

          {labels.length > 0 && (
            <div>
              <label className="block text-xs font-bold text-ink mb-1.5 dark:text-slate-200">
                {t('subs.field.labels') || 'Assign Labels'}
              </label>
              <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                {labels.map((l) => {
                  const selected = selectedLabels.includes(l.id);
                  return (
                    <button
                      key={l.id}
                      type="button"
                      onClick={() => toggleLabel(l.id)}
                      className={`rounded-full px-2.5 py-1 text-xs font-bold transition-all border ${
                        selected
                          ? 'bg-brand text-white border-brand shadow-sm'
                          : 'bg-surface text-slate-700 border-line hover:bg-slate-100 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300'
                      }`}
                    >
                      {selected ? '✓ ' : ''}{l.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-line px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 dark:border-slate-800 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              {t('common.cancel') || 'Cancel'}
            </button>
            <button
              type="submit"
              disabled={submitting || !phone.trim()}
              className="flex items-center gap-1.5 rounded-xl bg-brand px-5 py-2 text-xs font-bold text-white shadow-sm hover:bg-brand-dark disabled:opacity-50"
            >
              <CheckIcon size={14} />
              <span>{submitting ? t('common.saving') || 'Saving…' : t('subs.add') || 'Add Subscriber'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
